import {
  Card,
  Grid,
  Stack,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  MenuItem,
} from '@mui/material';
import { Box } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import AutoComplete from '../../../components/AutoComplete';
import { useAppDispatch, useAppSelector } from '../../../store';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  CreatePatchAdvertise,
  Advertise as IAdvertise,
} from '../../../store/advertise/api/interface';
import { FormikProvider, useFormik } from 'formik';
import {
  createAdvertise,
  getAdvertise,
  patchAdvertise,
} from '../../../store/advertise/api';
import { setStatus } from '../../../store/advertise/advertiseSlice';
import { useEffect, useMemo, useRef, useState } from 'react';
import { decryptString } from '../../../utils/url';
import Loader from '../../../components/Loader';
import { omit, uniqBy } from 'lodash';
import { NumericFormatCustom } from '../../../components/NumericCustomFormatter';
import {
  findSelectPropertyCategory,
  searchSelectPropertyCategory,
} from '../../../store/selectPropertyCategory/api';
import {
  setSkip as setSkipPropertyCategory,
  reset as resetPropertyCategory,
} from '../../../store/selectPropertyCategory/selectPropertyCategorySlice';
import { DocumentType, PropertyPossessionType } from '../../../enum';
import {
  findSelectPropertySubCategory,
  searchSelectPropertySubCategory,
} from '../../../store/selectPropertySubCategory/api';
import {
  setSkip as setSkipPropertySubCategory,
  reset as resetPropertySubCategory,
  setStatus as setSelectStatusPropertySubCategory,
} from '../../../store/selectPropertySubCategory/selectPropertySubCategorySlice';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import useToggle from '../../../hooks/useToggle';
import UploadFormModal from '../../../components/UploadAssetsModal';
import advertiseSchema from '../../../validation/advertiseSchema';
import {
  GoogleMap,
  Marker,
  useJsApiLoader,
  Polygon,
} from '@react-google-maps/api';
import {
  findSelectPossessionType,
  searchSelectPossessionType,
} from '../../../store/selectPossessionType/api';
import {
  reset as resetPossessionType,
  setSkip as setSkipPossessionType,
} from '../../../store/selectPossessionType/selectPossessionTypeSlice';
import {
  findSelectUsers,
  searchSelectUsers,
} from '../../../store/selectUser/api';
import {
  reset as resetUsers,
  setSkip as setSkipUsers,
} from '../../../store/selectUser/selectUserSlice';
import MultipleAutoComplete from '../../../components/MultipleAutoComplete';
import {
  findSelectPropertyFacility,
  searchSelectPropertyFacility,
} from '../../../store/selectPropertyFacility/api';
import { PropertyFacility } from '../../../store/propertyFacility/api/interface';
import {
  reset as resetPropertyFacility,
  setSkip as setSkipPropertyFacility,
  setStatus as setSelectStatusPropertyFacility,
} from '../../../store/selectPropertyFacility/selectPropertyFacilitySlice';
import DetailsForm from './DetailsForm';
import {
  findSelectLocationManagement,
  searchSelectLocationManagement,
} from '../../../store/selectLocationManagement/api';
import {
  reset as resetLocation,
  setSkip as setSkipLocation,
} from '../../../store/selectLocationManagement/selectLocationManagementSlice';
import { findLocationCoordinate } from '../../../store/getLocationCoordinate/api';
import PinImage from '../../../assets/pin.png';
import { RENT_FREQUENCY } from '../../../constants';
import {
  setStatus as setSelectStatusPropertyFeature,
  reset as resetSelectPropertyFeature,
} from '../../../store/selectPropertyFeature/selectPropertyFeatureSlice';

// Keep libraries array outside component to prevent re-renders
const GOOGLE_MAPS_LIBRARIES: ('places')[] = ['places'];

const AdvertiseForm = () => {
  const dispatch = useAppDispatch();
  const navigation = useNavigate();
  const params: any = useParams();
  const location = useLocation();
  const state = location.state as IAdvertise; // Type Casting, then you can get the params passed via router

  const isAddMode = !params?.id;
  const { isLoading } = useAppSelector((state) => state.advertise);
  const mapRef: any = useRef(null);
  const [response, setResponse] = useState<any>();
  const [attachmentArray, setAttachmentArray] = useState([]);
  const [isLand, setIsLand] = useState(false);
  const [polygonCoordinates, setPolygonCoordinates] = useState<any[]>([]);
  const [zoom, setZoom] = useState<number>(9);
  const [mapCoordinates, setMapCoordinates] =
    useState<google.maps.LatLngLiteral>({
      lat: 25.3548,
      lng: 51.1839,
    });
  const [propertyFacility, setPropertyFacility] = useState<PropertyFacility[]>(
    []
  );
  const { toggle, onOpen, onClose } = useToggle();
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY ?? '',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const markerIcon = useMemo(() => {
    if (window.google) {
      return {
        url: PinImage, // Custom icon URL
        scaledSize: new window.google.maps.Size(30, 40), // Custom size (50x50 in this example)
      };
    }
    return undefined;
  }, [isLoaded]);

  const formik = useFormik<CreatePatchAdvertise>({
    initialValues: {
      id: '',
      title: '',
      areaPolygon: '',
      description: '',
      zipCode: '',
      zoneNumber: '',
      streetNumber: '',
      buildingNumber: '',
      address: '',
      latitude: 0,
      longitude: 0,
      possessionTypeId: '',
      rentFrequency: '',
      price: '',
      status: 'draft',
      categoryId: '',
      subCategoryId: '',
      userId: '',
      locationId: '',
      attachmentArray: [],
      propertyFacilityIds: [],
      propertyFeatureItems: [
        {
          featureId: '',
          value: '',
        },
      ],
    },
    validationSchema: advertiseSchema,
    onSubmit: async (values) => {
      const buildRequestBody = (payload: CreatePatchAdvertise) => {
        const reqBody: any = omit(payload, ['id']);
        const uuidFields = [
          'locationId',
          'categoryId',
          'subCategoryId',
          'possessionTypeId',
          'titleDeedAssetId',
          'individualIdAssetId',
        ];

        uuidFields.forEach((field) => {
          if (reqBody[field] === '') {
            delete reqBody[field];
          }
        });

        if (reqBody.areaPolygon === '') {
          delete reqBody.areaPolygon;
        }
        if (reqBody.rentFrequency === '') {
          delete reqBody.rentFrequency;
        }
        if (reqBody.propertyFeatureItems) {
          reqBody.propertyFeatureItems = reqBody.propertyFeatureItems.filter(
            (item: { featureId?: string }) => Boolean(item?.featureId)
          );
        }

        return reqBody;
      };

      try {
        if (isAddMode) {
          await dispatch(createAdvertise(buildRequestBody(values))).unwrap();
        }
        if (!isAddMode) {
          const reqBody: any = buildRequestBody(values);
          delete reqBody.createdAt;
          delete reqBody.createdBy;
          delete reqBody.updatedAt;
          delete reqBody.updatedBy;
          delete reqBody.property_category;
          await dispatch(patchAdvertise(reqBody)).unwrap();
        }
        formik?.resetForm();
        dispatch(setStatus({ status: 'idle' }));
        navigation('/advertise');
      } catch (error: any) {}
    },
  });

  useEffect(() => {
    const fetchById = async () => {
      if (isAddMode) return;

      if (state) {
        if (state?.latitude !== 0 && state?.longitude !== 0) {
          setMapCoordinates({ lat: state.latitude, lng: state.longitude });
        }
        // setValues(state);
        setResponse(state);
      } else {
        try {
          const decodeParam = decryptString(params.id);
          const record = await dispatch(
            getAdvertise({ id: decodeParam })
          ).unwrap();
          if (record?.latitude !== 0 && record?.longitude !== 0) {
            setMapCoordinates({ lat: record.latitude, lng: record.longitude });
          }
          // setValues(record);
          setResponse(record);
        } catch (error: any) {
          navigation('/advertise');
        }
      }
    };
    fetchById();
  }, [dispatch, isAddMode, navigation, params?.id, formik?.setValues, state]);

  useEffect(() => {
    formik?.setFieldValue('attachmentArray', attachmentArray);
  }, [attachmentArray]);

  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      formik?.setFieldValue('areaPolygon', '');
      setPolygonCoordinates([]);
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      formik?.setFieldValue('latitude', lat);
      formik?.setFieldValue('longitude', lng);
      setMapCoordinates({ lat, lng });
    }
  };

  const getAddress = async () => {
    if (isLand) {
      if (formik?.values?.zipCode !== '') {
        const areaPolygonResult = await dispatch(
          findLocationCoordinate({ zipCode: formik?.values?.zipCode })
        ).unwrap();
        if (areaPolygonResult?.features?.[0]?.geometry?.rings) {
          const coordinatesArray: string[] = [];

          for (const key in areaPolygonResult?.features?.[0]?.geometry?.rings) {
            // Step 2: Get the array of coordinates using the key
            const coordinates =
              areaPolygonResult?.features?.[0]?.geometry?.rings[key];

            // Step 3: Loop through each coordinate pair in the array
            for (let i = 0; i < coordinates.length; i++) {
              const coord = coordinates[i];
              coordinatesArray.push(`${coord[0]} ${coord[1]}`); // Add each "longitude latitude" pair to the array
            }

            const formattedCoords = coordinates.map((coord: string) => ({
              lat: coord[1],
              lng: coord[0],
            }));
            setPolygonCoordinates(formattedCoords);
            const firstItemCoordinate = coordinates[0];
            formik?.setFieldValue('latitude', firstItemCoordinate[1]);
            formik?.setFieldValue('longitude', firstItemCoordinate[0]);
            const newCenter = {
              lat: firstItemCoordinate[1],
              lng: firstItemCoordinate[0],
            };
            mapRef?.current?.panTo(newCenter);
            setZoom(15);
          }
          formik?.setFieldValue(
            'areaPolygon',
            `POLYGON((${coordinatesArray.join(',')}))`
          );
        }
      }
    } else {
      if (
        formik?.values?.zoneNumber !== '' &&
        formik?.values?.buildingNumber !== '' &&
        formik?.values?.streetNumber !== ''
      ) {
        const areaLatLongResult = await dispatch(
          findLocationCoordinate({
            zoneNumber: formik?.values?.zoneNumber,
            buildingNumber: formik?.values?.buildingNumber,
            streetNumber: formik?.values?.streetNumber,
          })
        ).unwrap();
        if (areaLatLongResult?.features?.[0]?.geometry) {
          const newCenter = {
            lat: areaLatLongResult?.features?.[0]?.geometry?.y,
            lng: areaLatLongResult?.features?.[0]?.geometry?.x,
          };
          mapRef?.current?.panTo(newCenter);
          setZoom(15);
          formik?.setFieldValue(
            'latitude',
            areaLatLongResult?.features?.[0]?.geometry?.y
          );
          formik?.setFieldValue(
            'longitude',
            areaLatLongResult?.features?.[0]?.geometry?.x
          );
          const lat = areaLatLongResult?.features?.[0]?.geometry?.y as number;
          const lng = areaLatLongResult?.features?.[0]?.geometry?.x as number;
          setMapCoordinates({ lat, lng });
        }
      }
    }
  };

  // Property Category Data
  const {
    data: propertyCategoryData,
    isLoading: isPropertyCategoryLoading,
    skip: propertyCategorySkip,
    hasMore: propertyCategoryHasMore,
    status: propertyCategoryStatus,
  } = useAppSelector((state) => state.selectPropertyCategory);

  useEffect(() => {
    if (propertyCategoryStatus === 'idle') {
      dispatch(
        findSelectPropertyCategory({
          limit: 10,
          skip: propertyCategorySkip,
          activeOnly: true,
        })
      );
    }
  }, [propertyCategorySkip, propertyCategoryStatus]);

  const onScrollFetchPropertyCategory = (
    e: React.UIEvent<HTMLUListElement, UIEvent>,
    search: string
  ) => {
    if (
      e.currentTarget.scrollTop + 300 >= e.currentTarget.scrollHeight &&
      propertyCategoryHasMore
    ) {
      dispatch(setSkipPropertyCategory({ skip: propertyCategorySkip + 10 }));
      dispatch(
        findSelectPropertyCategory({
          limit: 10,
          skip: propertyCategorySkip + 10,
          ...(search && { search: search }),
          activeOnly: true,
        })
      );
    }
  };

  const searchPropertyCategory = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!e.target.value) {
      dispatch(resetPropertyCategory());
      return;
    }
    const params = {
      activeOnly: true,
      ...(!e.target.value && { limit: 10 }),
      ...(!e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(setSkipPropertyCategory({ skip: 0 }));
    dispatch(searchSelectPropertyCategory(params));
  };

  const uniquePropertyCategoryData = uniqBy(
    [
      ...(response?.property_category ? [response?.property_category] : []),
      ...propertyCategoryData,
    ],
    'id'
  );

  // Property Sub Category Data
  const {
    data: propertySubCategoryData,
    isLoading: isPropertySubCategoryLoading,
    skip: propertySubCategorySkip,
    hasMore: propertySubCategoryHasMore,
    status: propertySubCategoryStatus,
  } = useAppSelector((state) => state.selectPropertySubCategory);

  useEffect(() => {
    if (formik?.values?.categoryId && propertySubCategoryStatus === 'idle') {
      dispatch(
        findSelectPropertySubCategory({
          categoryId: formik?.values?.categoryId,
          limit: 10,
          skip: propertySubCategorySkip,
          activeOnly: true,
        })
      );
    }
  }, [
    propertySubCategorySkip,
    formik?.values?.categoryId,
    propertySubCategoryStatus,
  ]);

  const onScrollFetchPropertySubCategory = (
    e: React.UIEvent<HTMLUListElement, UIEvent>,
    search: string
  ) => {
    if (
      e.currentTarget.scrollTop + 300 >= e.currentTarget.scrollHeight &&
      propertySubCategoryHasMore
    ) {
      dispatch(
        setSkipPropertySubCategory({ skip: propertySubCategorySkip + 10 })
      );
      dispatch(
        findSelectPropertySubCategory({
          categoryId: formik?.values?.categoryId,
          limit: 10,
          skip: propertySubCategorySkip + 10,
          ...(search && { search: search }),
          activeOnly: true,
        })
      );
    }
  };

  const searchPropertySubCategory = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!e.target.value) {
      dispatch(resetPropertySubCategory());
      return;
    }
    const params = {
      activeOnly: true,
      categoryId: formik?.values?.categoryId,
      ...(!e.target.value && { limit: 10 }),
      ...(!e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(setSkipPropertySubCategory({ skip: 0 }));
    dispatch(searchSelectPropertySubCategory(params));
  };

  const uniquePropertySubCategoryData = uniqBy(
    [
      // ...(response?.property_sub_category ? [response?.property_sub_category] : []),
      ...propertySubCategoryData,
    ],
    'id'
  );

  // Possession Type Data
  const {
    data: possessionTypeData,
    isLoading: isPossessionTypeLoading,
    skip: possessionTypeSkip,
    hasMore: possessionTypeHasMore,
    status: possessionTypeStatus,
  } = useAppSelector((state) => state.selectPossessionType);

  useEffect(() => {
    if (possessionTypeStatus === 'idle') {
      dispatch(
        findSelectPossessionType({
          limit: 10,
          skip: possessionTypeSkip,
        })
      );
    }
  }, [possessionTypeSkip, possessionTypeStatus]);

  const onScrollFetchPossessionType = (
    e: React.UIEvent<HTMLUListElement, UIEvent>,
    search: string
  ) => {
    if (
      e.currentTarget.scrollTop + 300 >= e.currentTarget.scrollHeight &&
      possessionTypeHasMore
    ) {
      dispatch(setSkipPossessionType({ skip: possessionTypeSkip + 10 }));
      dispatch(
        findSelectPossessionType({
          limit: 10,
          skip: possessionTypeSkip + 10,
          ...(search && { search: search }),
        })
      );
    }
  };

  const searchPossessionType = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!e.target.value) {
      dispatch(resetPossessionType());
      return;
    }
    const params = {
      ...(!e.target.value && { limit: 10 }),
      ...(!e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(setSkipPossessionType({ skip: 0 }));
    dispatch(searchSelectPossessionType(params));
  };

  const uniquePossessionTypeData = uniqBy([...possessionTypeData], 'id');

  // Location Data
  const {
    data: locationData,
    isLoading: isLocationLoading,
    skip: locationSkip,
    hasMore: locationHasMore,
    status: locationStatus,
  } = useAppSelector((state) => state.selectLocationManagement);

  useEffect(() => {
    if (locationStatus === 'idle') {
      dispatch(
        findSelectLocationManagement({
          limit: 10,
          skip: locationSkip,
        })
      );
    }
  }, [locationSkip, locationStatus]);

  const onScrollFetchLocation = (
    e: React.UIEvent<HTMLUListElement, UIEvent>,
    search: string
  ) => {
    if (
      e.currentTarget.scrollTop + 300 >= e.currentTarget.scrollHeight &&
      locationHasMore
    ) {
      dispatch(setSkipLocation({ skip: locationSkip + 10 }));
      dispatch(
        findSelectLocationManagement({
          limit: 10,
          skip: locationSkip + 10,
          ...(search && { search: search }),
        })
      );
    }
  };

  const searchLocation = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!e.target.value) {
      dispatch(resetLocation());
      return;
    }
    const params = {
      ...(!e.target.value && { limit: 10 }),
      ...(!e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(setSkipLocation({ skip: 0 }));
    dispatch(searchSelectLocationManagement(params));
  };

  const uniqueLocationData = uniqBy([...locationData], 'id');

  // User Data
  const {
    data: userData,
    isLoading: isUserLoading,
    skip: userSkip,
    hasMore: userHasMore,
    status: userStatus,
  } = useAppSelector((state) => state.selectUser);

  useEffect(() => {
    if (userStatus === 'idle') {
      dispatch(
        findSelectUsers({
          isUser: true,
          limit: 10,
          skip: userSkip,
        })
      );
    }
  }, [userSkip, userStatus]);

  const onScrollFetchUser = (
    e: React.UIEvent<HTMLUListElement, UIEvent>,
    search: string
  ) => {
    if (
      e.currentTarget.scrollTop + 300 >= e.currentTarget.scrollHeight &&
      userHasMore
    ) {
      dispatch(setSkipUsers({ skip: userSkip + 10 }));
      dispatch(
        findSelectUsers({
          isUser: true,
          limit: 10,
          skip: userSkip + 10,
          ...(search && { search: search }),
        })
      );
    }
  };

  const searchUsers = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!e.target.value) {
      dispatch(resetUsers());
      return;
    }
    const params = {
      isUser: true,
      ...(!e.target.value && { limit: 10 }),
      ...(!e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(setSkipUsers({ skip: 0 }));
    dispatch(searchSelectUsers(params));
  };

  const uniqueUsersData = uniqBy([...userData], 'id');

  // Property Facility
  const {
    data: propertyFacilityData,
    isLoading: isPropertyFacilityLoading,
    skip: skipPropertyFacility,
    hasMore: hasMorePropertyFacility,
    status: propertyFacilityStatus,
  } = useAppSelector((state) => state.selectPropertyFacility);

  useEffect(() => {
    if (formik?.values?.categoryId && propertyFacilityStatus === 'idle') {
      dispatch(
        findSelectPropertyFacility({
          limit: 10,
          skip: skipPropertyFacility,
          propertyCategoryId: formik?.values?.categoryId,
          ...(formik?.values?.subCategoryId && {
            propertySubCategoryId: formik?.values?.subCategoryId,
          }),
          activeOnly: true,
        })
      );
    }
  }, [
    skipPropertyFacility,
    formik?.values?.categoryId,
    formik?.values?.subCategoryId,
    propertyFacilityStatus,
  ]);

  const onScrollFetchPropertyFacility = (
    event: React.UIEvent<HTMLElement, UIEvent>
  ) => {
    if (
      event.currentTarget.scrollTop + 300 >= event.currentTarget.scrollHeight &&
      hasMorePropertyFacility
    ) {
      dispatch(setSkipPropertyFacility({ skip: skipPropertyFacility + 10 }));
      dispatch(setSelectStatusPropertyFacility({ status: 'idle' }));
    }
  };

  const searchPropertyFacility = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      dispatch(resetPropertyFacility());
      return;
    }
    const params = {
      activeOnly: true,
      propertyCategoryId: formik?.values?.categoryId,
      ...(formik?.values?.subCategoryId && {
        propertySubCategoryId: formik?.values?.subCategoryId,
      }),
      ...(e.target.value && { limit: 50 }),
      ...(e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(setSkipPropertyFacility({ skip: 0 }));
    dispatch(searchSelectPropertyFacility(params));
  };

  const uniquePropertyFacilityData = uniqBy(
    [
      ...(propertyFacility?.length > 0 ? [...propertyFacility] : []),
      ...propertyFacilityData,
    ],
    'id'
  );

  return (
    <Grid
      container
      spacing={3}
      mt={1.5}
    >
      <Grid
        item
        xs={12}
        md={12}
      >
        {isLoading ? (
          <Loader />
        ) : (
          <FormikProvider value={formik}>
            <form onSubmit={formik?.handleSubmit}>
              <Card sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: {
                      xs: 'repeat(1, 1fr)',
                      sm: 'repeat(2, 1fr)',
                    },
                  }}
                >
                  <TextField
                    fullWidth
                    label={'Title'}
                    name='title'
                    id='title'
                    onChange={formik?.handleChange}
                    value={formik?.values.title}
                    error={Boolean(
                      formik?.errors.title && formik?.touched.title
                    )}
                    helperText={
                      Boolean(formik?.errors.title && formik?.touched.title) &&
                      formik?.errors.title
                    }
                  />

                  <TextField
                    fullWidth
                    label={'Description'}
                    name='description'
                    id='description'
                    onChange={formik?.handleChange}
                    value={formik?.values.description}
                    error={Boolean(
                      formik?.errors.description && formik?.touched.description
                    )}
                    helperText={
                      Boolean(
                        formik?.errors.description &&
                          formik?.touched.description
                      ) && formik?.errors.description
                    }
                  />

                  <AutoComplete
                    key={`procat-${formik?.values?.categoryId}`}
                    onChange={(event, newValue) => {
                      if (typeof newValue === 'string') {
                      } else if (newValue) {
                        setIsLand(newValue.isLand);
                        dispatch(resetPropertySubCategory());
                        dispatch(
                          setSelectStatusPropertySubCategory({ status: 'idle' })
                        );
                        dispatch(resetPropertyFacility());
                        dispatch(
                          setSelectStatusPropertyFacility({ status: 'idle' })
                        );
                        dispatch(resetSelectPropertyFeature());
                        dispatch(
                          setSelectStatusPropertyFeature({ status: 'idle' })
                        );
                        formik?.setFieldValue('propertyFeatureItems', [
                          {
                            featureId: '',
                            value: '',
                          },
                        ]);
                        setPropertyFacility([]);
                        formik?.setFieldValue('propertyFacilityIds', []);
                        formik?.setFieldValue('categoryId', newValue.id);
                      }
                    }}
                    onInputChange={(event, newInputValue) => {
                      if (!newInputValue) {
                        dispatch(resetPropertyFacility());
                        dispatch(
                          setSelectStatusPropertyFacility({ status: 'idle' })
                        );
                        dispatch(resetSelectPropertyFeature());
                        dispatch(
                          setSelectStatusPropertyFeature({ status: 'idle' })
                        );
                        formik?.setFieldValue('categoryId', '');
                        setIsLand(false);
                        formik?.setFieldValue('zipCode', '');
                        formik?.setFieldValue('zoneNumber', '');
                        formik?.setFieldValue('buildingNumber', '');
                        formik?.setFieldValue('streetNumber', '');
                        formik?.setFieldValue('propertyFeatureItems', [
                          {
                            featureId: '',
                            value: '',
                          },
                        ]);
                        setPropertyFacility([]);
                        formik?.setFieldValue('propertyFacilityIds', []);
                      }
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>{option.labelEn}</li>
                    )}
                    getOptionLabel={(option) => option.labelEn}
                    error={Boolean(
                      formik?.touched.categoryId && formik?.errors.categoryId
                    )}
                    helperText={
                      Boolean(
                        formik?.touched.categoryId && formik?.errors.categoryId
                      )
                        ? formik?.errors.categoryId
                        : ''
                    }
                    fetchFunction={onScrollFetchPropertyCategory}
                    label={'Select Property Category'}
                    loading={isPropertyCategoryLoading}
                    options={uniquePropertyCategoryData}
                    searchFunction={searchPropertyCategory}
                    value={
                      uniquePropertyCategoryData?.filter(
                        (category) => category.id === formik?.values.categoryId
                      )?.[0] || null
                    }
                  />

                  <AutoComplete
                    key={`prosubcat-${formik?.values?.subCategoryId}`}
                    onChange={(event, newValue) => {
                      if (typeof newValue === 'string') {
                      } else if (newValue) {
                        dispatch(resetPropertyFacility());
                        dispatch(
                          setSelectStatusPropertyFacility({ status: 'idle' })
                        );
                        dispatch(resetSelectPropertyFeature());
                        dispatch(
                          setSelectStatusPropertyFeature({ status: 'idle' })
                        );
                        formik?.setFieldValue('propertyFeatureItems', [
                          {
                            featureId: '',
                            value: '',
                          },
                        ]);
                        setPropertyFacility([]);
                        formik?.setFieldValue('propertyFacilityIds', []);
                        formik?.setFieldValue('subCategoryId', newValue.id);
                      }
                    }}
                    onInputChange={(event, newInputValue) => {
                      if (!newInputValue) {
                        dispatch(resetPropertyFacility());
                        dispatch(
                          setSelectStatusPropertyFacility({ status: 'idle' })
                        );
                        dispatch(resetSelectPropertyFeature());
                        dispatch(
                          setSelectStatusPropertyFeature({ status: 'idle' })
                        );
                        formik?.setFieldValue('subCategoryId', '');
                        setPropertyFacility([]);
                        formik?.setFieldValue('propertyFacilityIds', []);
                        formik?.setFieldValue('propertyFeatureItems', [
                          {
                            featureId: '',
                            value: '',
                          },
                        ]);
                      }
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>{option.labelEn}</li>
                    )}
                    getOptionLabel={(option) => option.labelEn}
                    error={Boolean(
                      formik?.touched.subCategoryId &&
                        formik?.errors.subCategoryId
                    )}
                    helperText={
                      Boolean(
                        formik?.touched.subCategoryId &&
                          formik?.errors.subCategoryId
                      )
                        ? formik?.errors.subCategoryId
                        : ''
                    }
                    fetchFunction={onScrollFetchPropertySubCategory}
                    label={'Select Property Sub Category'}
                    loading={isPropertySubCategoryLoading}
                    options={uniquePropertySubCategoryData}
                    searchFunction={searchPropertySubCategory}
                    value={
                      uniquePropertySubCategoryData?.filter(
                        (subCategory) =>
                          subCategory.id === formik?.values.subCategoryId
                      )?.[0] || null
                    }
                  />

                  <AutoComplete
                    key={`user-${formik?.values?.userId}`}
                    onChange={(event, newValue) => {
                      if (typeof newValue === 'string') {
                      } else if (newValue) {
                        formik?.setFieldValue('userId', newValue.id);
                      }
                    }}
                    onInputChange={(event, newInputValue) => {
                      if (!newInputValue) {
                        formik?.setFieldValue('userId', '');
                      }
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>{option.name}</li>
                    )}
                    getOptionLabel={(option) => option.name}
                    error={Boolean(
                      formik?.touched.userId && formik?.errors.userId
                    )}
                    helperText={
                      Boolean(formik?.touched.userId && formik?.errors.userId)
                        ? formik?.errors.userId
                        : ''
                    }
                    fetchFunction={onScrollFetchUser}
                    label={'Select User'}
                    loading={isUserLoading}
                    options={uniqueUsersData}
                    searchFunction={searchUsers}
                    value={
                      uniqueUsersData?.filter(
                        (user) => user.id === formik?.values.userId
                      )?.[0] || null
                    }
                  />

                  <AutoComplete
                    key={`toC-${formik?.values?.locationId}`}
                    onChange={(event, newValue) => {
                      if (typeof newValue === 'string') {
                      } else if (newValue) {
                        formik?.setFieldValue('locationId', newValue.id);
                      } else {
                        // Clear when value is null
                        formik?.setFieldValue('locationId', '');
                      }
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      // Only clear locationId when input is cleared, not when selecting
                      if (reason === 'clear' || (reason === 'input' && !newInputValue)) {
                        formik?.setFieldValue('locationId', '');
                      }
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>{option.labelEn}</li>
                    )}
                    getOptionLabel={(option) => option.labelEn}
                    error={Boolean(
                      formik?.touched.locationId && formik?.errors.locationId
                    )}
                    helperText={
                      Boolean(
                        formik?.touched.locationId && formik?.errors.locationId
                      )
                        ? formik?.errors.locationId
                        : ''
                    }
                    fetchFunction={onScrollFetchLocation}
                    label={'Select Location'}
                    loading={isLocationLoading}
                    options={uniqueLocationData}
                    searchFunction={searchLocation}
                    value={
                      uniqueLocationData?.filter(
                        (location) => location.id === formik?.values.locationId
                      )?.[0] || null
                    }
                  />

                  <AutoComplete
                    key={`protype-${formik?.values?.possessionTypeId}`}
                    onChange={(event, newValue) => {
                      if (typeof newValue === 'string') {
                      } else if (newValue) {
                        formik?.setFieldValue('possessionTypeId', newValue.id);
                      }
                    }}
                    onInputChange={(event, newInputValue) => {
                      if (!newInputValue) {
                        formik?.setFieldValue('possessionTypeId', '');
                      }
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>{option.labelEn}</li>
                    )}
                    getOptionLabel={(option) => option.labelEn}
                    error={Boolean(
                      formik?.touched.possessionTypeId &&
                        formik?.errors.possessionTypeId
                    )}
                    helperText={
                      Boolean(
                        formik?.touched.possessionTypeId &&
                          formik?.errors.possessionTypeId
                      )
                        ? formik?.errors.possessionTypeId
                        : ''
                    }
                    fetchFunction={onScrollFetchPossessionType}
                    label={'Select Possession Type'}
                    loading={isPossessionTypeLoading}
                    options={uniquePossessionTypeData}
                    searchFunction={searchPossessionType}
                    value={
                      uniquePossessionTypeData?.filter(
                        (possessionType) =>
                          possessionType.id === formik?.values.possessionTypeId
                      )?.[0] || null
                    }
                  />

                  <TextField
                    fullWidth
                    label={'Price'}
                    name='price'
                    id='price'
                    onChange={formik?.handleChange}
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                    }}
                    value={formik?.values.price}
                    error={Boolean(
                      formik?.errors.price && formik?.touched.price
                    )}
                    helperText={
                      Boolean(formik?.errors.price && formik?.touched.price) &&
                      formik?.errors?.price
                    }
                  />

                  {formik?.values?.possessionTypeId ===
                    PropertyPossessionType.forRent && (
                    <FormControl
                      fullWidth
                      error={Boolean(
                        formik?.touched?.rentFrequency &&
                          formik?.errors?.rentFrequency
                      )}
                    >
                      <InputLabel>{'Rent Frequency'}</InputLabel>
                      <Select
                        name='rentFrequency'
                        labelId='rentFrequency'
                        id='rentFrequency'
                        value={formik?.values.rentFrequency}
                        label={'Rent Frequency'}
                        onChange={(e) =>
                          formik?.setFieldValue('rentFrequency', e.target.value)
                        }
                        MenuProps={{
                          style: {
                            maxHeight: 400,
                          },
                        }}
                      >
                        {RENT_FREQUENCY?.map((rentFrequency) => (
                          <MenuItem
                            key={rentFrequency.value}
                            value={rentFrequency.value}
                          >
                            {rentFrequency.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {Boolean(
                        formik?.touched.rentFrequency &&
                          formik?.errors.rentFrequency
                      ) && (
                        <FormHelperText>
                          {formik?.errors?.rentFrequency}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                </Box>

                <Box py={2}>
                  <MultipleAutoComplete
                    key={`proFac-${uniquePropertyFacilityData?.length}`}
                    onChange={(event, newValue, reason) => {
                      if (reason === 'removeOption') {
                        formik?.setFieldValue(
                          'propertyFacilityIds',
                          newValue?.map(
                            (propertyCategory) => propertyCategory.id
                          )
                        );
                        setPropertyFacility((propertyFacilityInfo) =>
                          propertyFacilityInfo.filter(
                            (propertyFacility) =>
                              propertyFacility?.id === newValue[0]?.id
                          )
                        );
                      }
                      if (typeof newValue === 'string') {
                      } else if (Array.isArray(newValue)) {
                        formik?.setFieldValue(
                          'propertyFacilityIds',
                          newValue?.map(
                            (propertyFacility) => propertyFacility?.id
                          )
                        );
                        setPropertyFacility((curr) =>
                          uniqBy([...curr, ...newValue], 'id')
                        );
                      }
                    }}
                    onInputChange={(event, newInputValue) => {
                      if (!newInputValue) {
                        formik?.setFieldValue('propertyFacilityIds', []);
                      }
                    }}
                    renderOption={(props, option: PropertyFacility) => (
                      <li {...props}>{option.labelEn}</li>
                    )}
                    getOptionLabel={(option: PropertyFacility) =>
                      option.labelEn
                    }
                    fetchFunction={onScrollFetchPropertyFacility}
                    label={'Select Property Facilities'}
                    onFocus={() => dispatch(setStatus({ status: 'idle' }))}
                    loading={isPropertyFacilityLoading}
                    options={uniquePropertyFacilityData}
                    searchFunction={searchPropertyFacility}
                    value={uniquePropertyFacilityData.filter(
                      (item: PropertyFacility) =>
                        formik?.values.propertyFacilityIds.includes(item.id)
                    )}
                    limitTags={4}
                    error={Boolean(
                      formik?.touched.propertyFacilityIds &&
                        formik?.errors.propertyFacilityIds
                    )}
                    helperText={
                      Boolean(
                        formik?.touched.propertyFacilityIds &&
                          formik?.errors.propertyFacilityIds
                      )
                        ? formik?.errors?.propertyFacilityIds
                        : ''
                    }
                  />
                </Box>

                <DetailsForm
                  isAddMode={isAddMode}
                  response={response}
                  values={formik.values}
                  handleChange={formik.handleChange}
                  setFieldValue={formik.setFieldValue}
                  errors={formik.errors}
                  touched={formik.touched}
                />

                <Box
                  sx={{
                    mt: 3,
                  }}
                >
                  <Typography
                    sx={{
                      mb: 2,
                      fontSize: '18px',
                    }}
                  >
                    Set Location:
                  </Typography>
                  {isLoaded && (
                    <>
                      <Grid
                        container
                        spacing={2}
                      >
                        {isLand ? (
                          <>
                            <Grid
                              item
                              lg={12}
                              md={12}
                              sm={12}
                              xs={12}
                            >
                              <TextField
                                fullWidth
                                label={'PIN Number'}
                                name='zipCode'
                                id='zipCode'
                                onChange={formik?.handleChange}
                                value={formik?.values.zipCode}
                                error={Boolean(
                                  formik?.errors.zipCode &&
                                    formik?.touched.zipCode
                                )}
                                helperText={
                                  Boolean(
                                    formik?.errors.zipCode &&
                                      formik?.touched.zipCode
                                  ) && formik?.errors.zipCode
                                }
                              />
                            </Grid>
                            <Grid
                              item
                              lg={8}
                              md={8}
                              sm={8}
                              xs={8}
                            ></Grid>
                            <Grid
                              item
                              lg={4}
                              md={4}
                              sm={4}
                              xs={4}
                            >
                              <Stack
                                direction={'row'}
                                display={'flex'}
                                justifyContent={'flex-end'}
                                alignItems='center'
                              >
                                <Button
                                  variant='contained'
                                  onClick={() => getAddress()}
                                >
                                  {'Get Address'}
                                </Button>
                              </Stack>
                            </Grid>
                          </>
                        ) : (
                          <>
                            <Grid
                              item
                              lg={4}
                              md={4}
                              sm={12}
                              xs={12}
                            >
                              <TextField
                                fullWidth
                                label={'Zone Number'}
                                name='zoneNumber'
                                id='zoneNumber'
                                onChange={formik?.handleChange}
                                value={formik?.values.zoneNumber}
                                error={Boolean(
                                  formik?.errors.zoneNumber &&
                                    formik?.touched.zoneNumber
                                )}
                                helperText={
                                  Boolean(
                                    formik?.errors.zoneNumber &&
                                      formik?.touched.zoneNumber
                                  ) && formik?.errors.zoneNumber
                                }
                              />
                            </Grid>
                            <Grid
                              item
                              lg={4}
                              md={4}
                              sm={12}
                              xs={12}
                            >
                              <TextField
                                fullWidth
                                label={'Street Number'}
                                name='streetNumber'
                                id='streetNumber'
                                onChange={formik?.handleChange}
                                value={formik?.values.streetNumber}
                                error={Boolean(
                                  formik?.errors.streetNumber &&
                                    formik?.touched.streetNumber
                                )}
                                helperText={
                                  Boolean(
                                    formik?.errors.streetNumber &&
                                      formik?.touched.streetNumber
                                  ) && formik?.errors.streetNumber
                                }
                              />
                            </Grid>
                            <Grid
                              item
                              lg={4}
                              md={4}
                              sm={12}
                              xs={12}
                            >
                              <TextField
                                fullWidth
                                label={'Building Number'}
                                name='buildingNumber'
                                id='buildingNumber'
                                onChange={formik?.handleChange}
                                value={formik?.values.buildingNumber}
                                error={Boolean(
                                  formik?.errors.buildingNumber &&
                                    formik?.touched.buildingNumber
                                )}
                                helperText={
                                  Boolean(
                                    formik?.errors.buildingNumber &&
                                      formik?.touched.buildingNumber
                                  ) && formik?.errors.buildingNumber
                                }
                              />
                            </Grid>
                            <Grid
                              item
                              lg={8}
                              md={8}
                              sm={8}
                              xs={8}
                            ></Grid>
                            <Grid
                              item
                              lg={4}
                              md={4}
                              sm={4}
                              xs={4}
                            >
                              <Stack
                                direction={'row'}
                                display={'flex'}
                                justifyContent={'flex-end'}
                                alignItems='center'
                              >
                                <Button
                                  variant='contained'
                                  onClick={() => getAddress()}
                                >
                                  {'Get Address'}
                                </Button>
                              </Stack>
                            </Grid>
                          </>
                        )}
                      </Grid>
                      <GoogleMap
                        mapContainerStyle={{
                          height: '400px',
                          width: '100%',
                          marginTop: '20px',
                        }}
                        center={mapCoordinates}
                        zoom={zoom}
                        onClick={handleMapClick}
                        onLoad={(map) => {
                          mapRef.current = map;
                        }}
                      >
                        {markerIcon && (
                          <Marker
                            position={mapCoordinates}
                            icon={markerIcon}
                          />
                        )}

                        <Polygon
                          paths={polygonCoordinates}
                          options={{
                            fillColor: '#a40466',
                            fillOpacity: 0.4,
                            strokeColor: '#a40466',
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                          }}
                        />
                      </GoogleMap>
                      {/* <Box sx={{ py: 2 }}>
                        <TextField
                          fullWidth
                          label={'Address'}
                          name='address'
                          id='address'
                          onChange={formik?.handleChange}
                          value={formik?.values.address}
                          error={Boolean(
                            formik?.errors.address && formik?.touched.address
                          )}
                          helperText={
                            Boolean(
                              formik?.errors.address && formik?.touched.address
                            ) && formik?.errors.address
                          }
                        />
                      </Box> */}
                    </>
                  )}
                </Box>

                <Box sx={{ py: 3 }}>
                  <Button
                    variant='contained'
                    startIcon={<CloudUploadIcon />}
                    onClick={onOpen}
                  >
                    {'Upload Image'}
                  </Button>
                  {formik?.errors.attachmentArray &&
                    formik?.touched.attachmentArray && (
                      <p
                        className='upload-error-message'
                        style={{ marginTop: '10px' }}
                      >
                        {formik?.errors?.attachmentArray}
                      </p>
                    )}
                </Box>

                <Stack
                  alignItems='center'
                  sx={{ mt: 3 }}
                  direction='row'
                  justifyContent='flex-end'
                  gap={1}
                >
                  <Button
                    variant='outlined'
                    onClick={() => navigation('/advertise')}
                  >
                    {'Cancel'}
                  </Button>

                  <LoadingButton
                    type='submit'
                    variant='contained'
                    loading={formik?.isSubmitting}
                  >
                    {isAddMode ? 'Add Advertise' : 'Edit Advertise'}
                  </LoadingButton>
                </Stack>
              </Card>
            </form>
          </FormikProvider>
        )}
      </Grid>

      <UploadFormModal
        toggle={toggle}
        onClose={onClose}
        setAttachmentArray={setAttachmentArray}
        module={DocumentType.Advertise}
        isSingleUpload={false}
      />
    </Grid>
  );
};

export default AdvertiseForm;
